import clsx from "clsx";
import { Fragment, ReactNode } from "react";

import { Check } from "@/assets/icons/icons";
import { Button } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import {
  Metric,
  MetricTiles,
} from "@/components/Layout/MetricTiles/MetricTiles";
import { DireProgressBar } from "@/components/Layout/ProgressBar/DireProgressBar";

export interface ProductCardListStep {
  label: string;
  status: "done" | "in-progress" | "to-be-done";
}

export type ProductCardFooterItemProps =
  | {
      variant: "button";
      label: string;
      icon?: ReactNode;
      onClick?: () => void;
      disabled?: boolean;
    }
  | {
      variant: "text-button";
      description: string;
      label: string;
      onClick?: () => void;
      disabled?: boolean;
      loading?: boolean;
    }
  | {
      variant: "qr-code";
      qrCode: ReactNode;
      description: string;
      link: ReactNode;
    }
  | { variant: "list"; title: string; steps: ProductCardListStep[] }
  | {
      variant: "progressbar";
      label: string;
      value: number;
      max: number;
      description: ReactNode;
    }
  | {
      variant: "notification";
      title: string;
      description?: string;
      tone?: "info" | "warning" | "error";
    }
  | { variant: "metric-cards"; cards: Metric[] };

const stepIndicatorClassName: Record<ProductCardListStep["status"], string> = {
  done: "bg-surface-accent-blue text-foreground-primary-inverse",
  "in-progress": "bg-surface-accent-blue text-foreground-primary-inverse",
  "to-be-done": "bg-surface-secondary",
};

const ProductCardListStepRow = ({ label, status }: ProductCardListStep) => (
  <div className="flex w-full items-center gap-2">
    <span
      className={clsx(
        "flex size-6 shrink-0 items-center justify-center rounded-full",
        stepIndicatorClassName[status],
      )}
    >
      {status === "done" && <Check className="size-4" />}
    </span>
    <p
      className={clsx(
        "truncate text-body-sm leading-md",
        status === "to-be-done"
          ? "text-foreground-tertiary"
          : "text-foreground-primary",
        status === "in-progress" && "font-semibold",
      )}
    >
      {label}
    </p>
  </div>
);

export interface ProductCardFooterBoxProps {
  children: ReactNode;
  className?: string;
  dataTestid?: string;
}

/**
 * The quaternary-background box shared by every "info panel" style footer
 * item (list/progressbar here, plus every device tile that composes its own
 * bespoke footer content instead of a fixed variant above).
 */
export const ProductCardFooterBox = ({
  children,
  className,
  dataTestid,
}: ProductCardFooterBoxProps) => (
  <div
    data-testid={dataTestid}
    className={clsx(
      "flex w-full flex-col gap-4 rounded-md bg-surface-quaternary p-4",
      // Tailwind resolves same-specificity utility conflicts by stylesheet
      // order, not by class-attribute order, so `items-start` and a
      // caller-supplied `items-*` override can't safely coexist in one
      // clsx() call -- omit the default whenever the caller supplies its own.
      // Only an unprefixed items-* token conflicts with the base items-start;
      // responsive/state variants like `md:items-center` apply on top of it
      // and must not suppress it.
      !className?.split(" ").some((token) => token.startsWith("items-")) &&
        "items-start",
      className,
    )}
  >
    {children}
  </div>
);

export const ProductCardFooterItem = (props: ProductCardFooterItemProps) => {
  switch (props.variant) {
    case "button":
      return (
        <Button
          variant="primary"
          fullWidth
          icon={props.icon}
          disabled={props.disabled}
          onClick={props.onClick}
        >
          {props.label}
        </Button>
      );

    case "text-button":
      return (
        <ProductCardFooterBox className="items-center text-center">
          <p className="text-body-base leading-md text-foreground-primary">
            {props.description}
          </p>
          <Button
            variant="primary"
            disabled={props.disabled}
            loading={props.loading}
            onClick={props.onClick}
          >
            {props.label}
          </Button>
        </ProductCardFooterBox>
      );

    case "qr-code":
      return (
        <div className="flex w-full items-start gap-4 rounded-md bg-surface-quaternary p-4">
          <div className="flex size-[88px] shrink-0 items-center justify-center rounded-md border border-border-tertiary p-2">
            {props.qrCode}
          </div>
          <div className="flex flex-1 flex-col items-start gap-2">
            <p className="text-body-base leading-md text-foreground-primary">
              {props.description}
            </p>
            {props.link}
          </div>
        </div>
      );

    case "list":
      return (
        <ProductCardFooterBox>
          <p className="w-full truncate text-headline-sm font-semibold leading-md text-foreground-primary">
            {props.title}
          </p>
          <div className="flex w-full flex-col items-start gap-1">
            {props.steps.map((step, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {index > 0 && (
                  <div className="flex w-6 shrink-0 justify-center">
                    <div className="h-2 w-px bg-border-tertiary" />
                  </div>
                )}
                <ProductCardListStepRow {...step} />
              </Fragment>
            ))}
          </div>
        </ProductCardFooterBox>
      );

    case "progressbar": {
      const usageBarWidth =
        Number.isFinite(props.value) &&
        Number.isFinite(props.max) &&
        props.max > 0
          ? Math.min(100, Math.max(0, (props.value / props.max) * 100))
          : undefined;

      return (
        <ProductCardFooterBox>
          <p className="text-body-base font-semibold leading-none text-foreground-primary">
            {props.label}
          </p>
          <div className="flex w-full flex-col items-start gap-2">
            {usageBarWidth !== undefined && (
              <DireProgressBar value={usageBarWidth} />
            )}
            <p className="text-body-sm leading-none text-foreground-primary">
              {props.description}
            </p>
          </div>
        </ProductCardFooterBox>
      );
    }

    case "notification":
      return (
        <Notification
          title={props.title}
          description={props.description}
          tone={props.tone}
        />
      );

    case "metric-cards":
      return <MetricTiles metrics={props.cards} gap={4} />;

    default:
      return null;
  }
};
